import { FormattedMessage, useIntl } from 'react-intl';
import { Form, FormInstance, Input, Tag } from 'antd';

import { StockItem } from '@/core/domain/stock/StockItems/StockItems.types';
import { getRowKey } from '@/core/domain/stock/StockItems/StockItems.utils';

import { CreateQuotationItemRequest } from '@/core/domain/stock/Quotations/Quotations.types';

import Table, { ColumnsType } from '@/components/_common/Table';
import { InputDecimal } from '@/components/_common';
import { RequestPurchases } from '@/core/domain/workorder/RequestPurchase/RequestPurchase.types';
import ManufacturerInfiniteSelectField from '@/components/_common/ManufacturerInfiniteSelectField';
import { getTableScrollX } from '@/utils/table';
import './style.less';

interface QuotationItemsSetUpStepProps {
  stockItems?: StockItem[];
  requestPurchases?: RequestPurchases[];
  form: FormInstance<CreateQuotationItemRequest>;
}

function QuotationItemsSetUpStep({
  stockItems,
  requestPurchases,
  form,
}: QuotationItemsSetUpStepProps) {
  const intl = useIntl();

  const columns: ColumnsType<StockItem> = [
    {
      key: 'manufacturingCode',
      dataIndex: 'manufacturingCode',
      ellipsis: true,
      width: 135,
      title: intl.formatMessage({
        id: 'quotations.details.items.manufacturingCode',
      }),
      render: (value: string) => value || '-',
    },
    {
      key: 'description',
      ellipsis: true,
      width: 320,
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'quotations.details.items.name',
      }),
      render: (value: string) => value || '-',
    },
    {
      key: 'manufacturer',
      dataIndex: 'manufacturer',
      title: intl.formatMessage({
        id: 'quotations.details.items.manufacturer',
      }),
      width: 140,
      ellipsis: true,
      className: 'td-form-item',
      render: (_, row, index) => {
        return (
          <Form.Item
            style={{ margin: 0 }}
            name={['items', +index, 'manufacturerId']}
            initialValue={row.manufacturer?.id}
            rules={[{ required: true, message: 'Obrigatório' }]}
          >
            <ManufacturerInfiniteSelectField />
          </Form.Item>
        );
      },
    },
    {
      key: 'quantity',
      dataIndex: 'quantity',
      title: intl.formatMessage({
        id: 'quotations.details.items.quantity',
      }),
      ellipsis: true,
      width: 130,
      className: 'td-form-item',
      render: (value, _, index) => (
        <Form.Item
          name={['items', +index, 'quantity']}
          initialValue={value}
          style={{ margin: 0 }}
          rules={[
            {
              type: 'number',
              min: 0.01,
              required: true,
              message: 'Maior que 0',
            },
          ]}
        >
          <InputDecimal min={0} />
        </Form.Item>
      ),
    },
    {
      key: 'type',
      width: 125,
      align: 'center',
      title: intl.formatMessage({
        id: 'quotations.details.items.create.filter.type',
      }),
      render: (_, row, index) => {
        const type = row.partId ? 'part' : 'consumable';

        return (
          <>
            <Form.Item
              hidden
              name={['items', +index, `${type}Id`]}
              initialValue={row.consumableId || row.partId}
            >
              <Input />
            </Form.Item>
            <Tag color="processing">
              <FormattedMessage
                id={`quotations.domain.enums.item_type.${type}`}
              />
            </Tag>
          </>
        );
      },
    },
  ];

  const columnsRequestPurchases: ColumnsType<RequestPurchases> = [
    {
      key: 'workOrderNumber',
      dataIndex: 'workOrderNumber',
      ellipsis: true,
      width: 135,
      title: intl.formatMessage({
        id: 'commons.table.column.workOrder',
      }),
      render: (value: string) => value || '-',
    },
    {
      key: 'description',
      ellipsis: true,
      width: 320,
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'quotations.details.items.name',
      }),
      render: (value: string) => value || '-',
    },
    {
      key: 'manufacturer',
      dataIndex: 'manufacturer',
      ellipsis: true,
      width: 200,
      title: intl.formatMessage({
        id: 'quotations.details.items.manufacturer',
      }),
      className: 'td-form-item',
      render: (_, row, index) => {
        return (
          <Form.Item
            style={{ margin: 0 }}
            name={['items', +index, 'manufacturerId']}
            initialValue={row.item.manufacturerId}
            rules={[{ required: true, message: 'Obrigatório' }]}
          >
            <ManufacturerInfiniteSelectField />
          </Form.Item>
        );
      },
    },
    {
      key: 'quantity',
      dataIndex: 'quantity',
      title: intl.formatMessage({
        id: 'quotations.details.items.quantity',
      }),
      width: 130,
      className: 'td-form-item',
      render: (_, row, index) => (
        <>
          <Form.Item
            hidden
            initialValue={row.workItemId}
            name={['items', index, 'workItemId']}
          />

          <Form.Item
            style={{ margin: 0 }}
            name={['items', +index, 'quantity']}
            initialValue={row.total || 0}
            rules={[
              {
                type: 'number',
                min: 0,
                required: true,
                message: 'Maior que 0',
              },
            ]}
          >
            <InputDecimal fractions={0} max={row.total || 0} min={0} />
          </Form.Item>
        </>
      ),
    },
  ];

  return (
    <Form form={form}>
      {requestPurchases?.length ? (
        <Table<RequestPurchases>
          columns={columnsRequestPurchases}
          dataSource={requestPurchases}
          scroll={{ x: getTableScrollX(columns) }}
          loading={false}
          rowKey={(row) => row.id}
        />
      ) : (
        <Table<StockItem>
          columns={columns}
          dataSource={stockItems}
          scroll={{ x: getTableScrollX(columns) }}
          loading={false}
          rowKey={(item) => getRowKey(item)}
        />
      )}
    </Form>
  );
}

export default QuotationItemsSetUpStep;
